<template>
  <div>
    <!-- Affichage d'une barre de progression pendant le chargement des données -->
    <load-progress :condition="!dataLoaded" label="Chargement des données"></load-progress>
    <!-- Affichage du tree view et des éditeurs selon l'objet sélectionné -->

    <div v-if="dataLoaded">
        <fiche-container ref="FICHE_CONTAINER"
                         :fiche="selectedFiche"
                         :route="notifRoute"
                         :attribut="attribut"
                         :route-element="routeElement"
                         @back="back"
                         @selectItem="selectItem">
        </fiche-container>
    </div>
    <div v-for="(button, index) in extraButtons" :key="index">
      <loader-button class="ms-2"
                     :outlined="button.outlined"
                     @callback="button.callback()"
                     :label="button.label"
                     :disabled="button.disabled">
      </loader-button>
    </div>
    <section-container :dialogOpen="showWizard"
                       :section="sectionWizard"
                       @closeDialog="hideWizard">
    </section-container>
  </div>
</template>

<script lang="ts">
  import { defineComponent, markRaw, type PropType } from "vue";
  import LoadProgress from '@/framework/base/components/LoadProgress.vue';
  import {
    storeSession,
    storeUser,
    storeAccidentTravail,
    storeAdresse,
    storeApprenti,
    storeAttestation,
    storeContact,
    storeDocument,
    storeEtablissement,
    storeNotif,
    storePaymentMeans,
    storeQualite,
    storeRib,
    storeSalarie,
    storeTransaction
  } from '@/generated/stores';
  import { CiRouter } from '@/framework/base/helpers/NavHelper';
  import Route from '@/generated/classes/Route';
  import ModuleBase from '@/framework/base/components/ModuleBase';
  import FicheEtablissementListExtend from '@/extend/fiches/FicheEtablissementListExtend.vue';
  import SectionEtablissementListExtend from '@/extend/sections/SectionEtablissementListExtend.vue';
  import FicheEtablissementAbstractExtend from '@/extend/fiches/FicheEtablissementAbstractExtend.vue';
  import SectionEtablissementIdentiteAbstractExtend from '@/extend/sections/SectionEtablissementIdentiteAbstractExtend.vue';
  import SectionEtablissementAdresseAbstractExtend from '@/extend/sections/SectionEtablissementAdresseAbstractExtend.vue';
  import SectionEtablissementRepresentantAbstractExtend from '@/extend/sections/SectionEtablissementRepresentantAbstractExtend.vue';
  import SectionEtablissementGestionnairesAbstractExtend from '@/extend/sections/SectionEtablissementGestionnairesAbstractExtend.vue';
  import SectionEtablissementJuridiqueAbstractExtend from '@/extend/sections/SectionEtablissementJuridiqueAbstractExtend.vue';
  import SectionDemandeAttestationAbstractExtend from '@/extend/sections/SectionDemandeAttestationAbstractExtend.vue';
  import SectionEtablissementAccidentTravailAbstractExtend from '@/extend/sections/SectionEtablissementAccidentTravailAbstractExtend.vue';
  import SectionEtablissementDocumentAbstractExtend from '@/extend/sections/SectionEtablissementDocumentAbstractExtend.vue';
  import SectionEtablissementPaymentAbstractExtend from '@/extend/sections/SectionEtablissementPaymentAbstractExtend.vue';
  import SectionAbonnement from '@/custom/sections/SectionAbonnement.vue';
  import SectionEtablissementCartAbstractExtend from '@/extend/sections/SectionEtablissementCartAbstractExtend.vue';
  import SectionEtablissementApprentiAbstractExtend from '@/extend/sections/SectionEtablissementApprentiAbstractExtend.vue';
  import FicheEtablissementExtend from '@/extend/fiches/FicheEtablissementExtend.vue';
  import SectionEtablissementIdentiteExtend from '@/extend/sections/SectionEtablissementIdentiteExtend.vue';
  import SectionAdresseExtend from '@/extend/sections/SectionAdresseExtend.vue';
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  import Owner from '@/generated/classes/Owner';
  import SectionEtablissementContactsExtend from '@/extend/sections/SectionEtablissementContactsExtend.vue';
  import SectionEtablissementAffiliationsExtend from '@/extend/sections/SectionEtablissementAffiliationsExtend.vue';
  import SectionEtablissementCaissesExtend from '@/extend/sections/SectionEtablissementCaissesExtend.vue';
  import SectionDemandeAttestationListExtend from '@/extend/sections/SectionDemandeAttestationListExtend.vue';
  import SectionAccidentTravailListExtend from '@/extend/sections/SectionAccidentTravailListExtend.vue';
  import SectionGestionnairesPaie from '@/custom/sections/SectionGestionnairesPaie.vue';
  import SectionApprentiListExtend from '@/extend/sections/SectionApprentiListExtend.vue';
  import SectionPaymentMeansListExtend from '@/extend/sections/SectionPaymentMeansListExtend.vue';
  import SectionPrelevMeansExtend from '@/extend/sections/SectionPrelevMeansExtend.vue';
  import SectionSalaireMeansExtend from '@/extend/sections/SectionSalaireMeansExtend.vue';
  import SectionReglementListExtend from '@/extend/sections/SectionReglementListExtend.vue';
  import SectionEtablissementLogoExtend from '@/extend/sections/SectionEtablissementLogoExtend.vue';
  import SectionEtablissementDocumentListExtend from '@/extend/sections/SectionEtablissementDocumentListExtend.vue';
  import { Fiches, Modules, Sections } from '@/generated/classes/AppSchema';
  import { GenModule, RessourceGenModule } from '@/generated/ressources/generatedRessources';
  import Wizard from '@/extend/classes/Wizard';
  import Section from '@/framework/base/classes/Section';
  import SectionWizard from '@/framework/wizard/components/SectionWizard.vue';
  import Fiche from '@/framework/base/classes/Fiche';
  import { RouteElements } from '@/framework/base/classes/Section';
  import User from '@/generated/classes/User';
  import FicheContainerAbstract from "@/framework/base/components/FicheContainerAbstract.vue";

  export function ModuleEtablissementOpenFiche(route: Route | undefined): RouteElements {
    const section = new Section();
    const fiche = new Fiche();
    if (route && route.fiche) {
      switch (route.fiche) {
        case Fiches.ETABLISSEMENT_LIST: {
          fiche.component = FicheEtablissementListExtend;
          fiche.type = 'etablissement'
          if (route && route.ficheObjId != null) {
            if (route.ficheObjId == 0) {
              fiche.data = storeEtablissement.createNewEtablissement();
            } else {
              fiche.data = storeEtablissement.getEtablissement(route.ficheObjId);
            }
          }
          break;
        }
        case Fiches.ETABLISSEMENT_ABSTRACT: {
          fiche.component = FicheEtablissementAbstractExtend;
          fiche.type = 'etablissement'
          if (route && route.ficheObjId != null) {
            if (route.ficheObjId == 0) {
              fiche.data = storeEtablissement.createNewEtablissement();
            } else {
              fiche.data = storeEtablissement.getEtablissement(route.ficheObjId);
            }
          }
          break;
        }
        case Fiches.ETABLISSEMENT: {
          fiche.component = FicheEtablissementExtend;
          fiche.type = 'etablissement'
          if (route && route.ficheObjId != null) {
            if (route.ficheObjId == 0) {
              fiche.data = storeEtablissement.createNewEtablissement();
            } else {
              fiche.data = storeEtablissement.getEtablissement(route.ficheObjId);
            }
          }
          break;
        }
        case Fiches.CONTAINER: {
          fiche.component = FicheContainerAbstract;
          fiche.type = 'etablissement'
          if (route && route.ficheObjId != null) {
            if (route.ficheObjId == 0) {
              fiche.data = storeEtablissement.createNewEtablissement();
            } else {
              fiche.data = storeEtablissement.getEtablissement(route.ficheObjId);
            }
          }
          break;
        }
      }
    } else {
      fiche.component = FicheEtablissementListExtend;
      fiche.type = 'etablissement'
      if (route && route.ficheObjId != null) {
        if (route.ficheObjId == 0) {
          fiche.data = storeEtablissement.createNewEtablissement();
        } else {
          fiche.data = storeEtablissement.getEtablissement(route.ficheObjId);
        }
      }
    }
    if (route && route.section && route.section != Sections.NONE) {
      switch (route.section) {
        case Sections.ETABLISSEMENT_LIST: {
          section.component = SectionEtablissementListExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_IDENTITE_ABSTRACT: {
          section.component = SectionEtablissementIdentiteAbstractExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_ADRESSE_ABSTRACT: {
          section.component = SectionEtablissementAdresseAbstractExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_REPRESENTANT_ABSTRACT: {
          section.component = SectionEtablissementRepresentantAbstractExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_GESTIONNAIRES_ABSTRACT: {
          section.component = SectionEtablissementGestionnairesAbstractExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_JURIDIQUE_ABSTRACT: {
          section.component = SectionEtablissementJuridiqueAbstractExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.DEMANDE_ATTESTATION_ABSTRACT: {
          section.component = SectionDemandeAttestationAbstractExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_ACCIDENT_TRAVAIL_ABSTRACT: {
          section.component = SectionEtablissementAccidentTravailAbstractExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_DOCUMENT_ABSTRACT: {
          section.component = SectionEtablissementDocumentAbstractExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_PAYMENT_ABSTRACT: {
          section.component = SectionEtablissementPaymentAbstractExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ABONNEMENT: {
          section.component = SectionAbonnement;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_CART_ABSTRACT: {
          section.component = SectionEtablissementCartAbstractExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_APPRENTI_ABSTRACT: {
          section.component = SectionEtablissementApprentiAbstractExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_IDENTITE: {
          section.component = SectionEtablissementIdentiteExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ADRESSE: {
          section.component = SectionAdresseExtend;
          section.type = 'adresse'
          if (route.sectionObjId == 0) {
            section.data = storeAdresse.createNewAdresse();
          } else {
            section.data = storeAdresse.getAdresse(route.sectionObjId);
          }
          section.data.owner = new Owner();
          section.data.owner.objType = ObjTypes.ETABLISSEMENT;
          if (route.ficheObjId) {
            section.data.owner.objId = route.ficheObjId;
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_CONTACTS: {
          section.component = SectionEtablissementContactsExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_AFFILIATIONS: {
          section.component = SectionEtablissementAffiliationsExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_CAISSES: {
          section.component = SectionEtablissementCaissesExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.DEMANDE_ATTESTATION_LIST: {
          section.component = SectionDemandeAttestationListExtend;
          section.type = 'attestation'
          if (route.sectionObjId == 0) {
            section.data = storeAttestation.createNewAttestation();
          } else {
            section.data = storeAttestation.getAttestation(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ACCIDENT_TRAVAIL_LIST: {
          section.component = SectionAccidentTravailListExtend;
          section.type = 'accidentTravail'
          if (route.sectionObjId == 0) {
            section.data = storeAccidentTravail.createNewAccidentTravail();
          } else {
            section.data = storeAccidentTravail.getAccidentTravail(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.GESTIONNAIRES_PAIE: {
          section.component = SectionGestionnairesPaie;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.APPRENTI_LIST: {
          section.component = SectionApprentiListExtend;
          section.type = 'apprenti'
          if (route.sectionObjId == 0) {
            section.data = storeApprenti.createNewApprenti();
          } else {
            section.data = storeApprenti.getApprenti(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.PAYMENT_MEANS_LIST: {
          section.component = SectionPaymentMeansListExtend;
          section.type = 'paymentMeans'
          if (route.sectionObjId == 0) {
            section.data = storePaymentMeans.createNewPaymentMeans();
          } else {
            section.data = storePaymentMeans.getPaymentMeans(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.PRELEV_MEANS: {
          section.component = SectionPrelevMeansExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.SALAIRE_MEANS: {
          section.component = SectionSalaireMeansExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.REGLEMENT_LIST: {
          section.component = SectionReglementListExtend;
          section.type = 'transaction'
          if (route.sectionObjId == 0) {
            section.data = storeTransaction.createNewTransaction();
          } else {
            section.data = storeTransaction.getTransaction(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_LOGO: {
          section.component = SectionEtablissementLogoExtend;
          section.type = 'etablissement'
          if (route.sectionObjId == 0) {
            section.data = storeEtablissement.createNewEtablissement();
          } else {
            section.data = storeEtablissement.getEtablissement(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.ETABLISSEMENT_DOCUMENT_LIST: {
          section.component = SectionEtablissementDocumentListExtend;
          section.type = 'document'
          if (route.sectionObjId == 0) {
            section.data = storeDocument.createNewDocument();
          } else {
            section.data = storeDocument.getDocument(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
      }
    }
    if (fiche && fiche.component) {
      fiche.component = markRaw(fiche.component);
    }
    if (section && section.component) {
      section.component = markRaw(section.component);
    }
    const res = new RouteElements();
    res.fiche = fiche;
    res.section = section;
    return res;
  }

  export default defineComponent({
    extends: ModuleBase,
    components: {
      LoadProgress,
    },

    props: {
    },

    created (): void {
      if (this.route) {
        this.notifRoute = this.route;
      }
      const session = storeSession.connectedUser;
      if (session) {
        const user = storeUser.getUser(session.id);
        if (user.modules.includes(Modules.ETABLISSEMENT)) {
          this.wizardDone = true;
        }
      }
      if (!this.wizardDone) {
        Wizard.showWizard(Modules.ETABLISSEMENT).then((res) => {
          if (res.id > 0) {
            this.wizard = res;
            this.showWizard = true;
          } else {
            User.acceptCgu(true);
            this.wizardDone = true;
          }
        })
      }
      this.loadStores();
    },

    data() {
      const attribut: unknown = undefined;
      const route = CiRouter.getRoute();
      return {
        wizard: new Wizard(),
        attribut,
        notifRoute: new Route(),
        route,
      }
    },

    computed: {
      ressource () {
        return RessourceGenModule(GenModule.MODULE_ETABLISSEMENT);
      },
      sectionWizard (): Section {
        const section = new Section();
        section.component = SectionWizard;
        section.data = this.wizard;
        section.helper = true;
        return section;
      },

      storesLoaded (): boolean {
        return true;
      },

      dataLoaded (): boolean {
        const res = this.wizardDone && this.storesLoaded
        if (res && this.init) {
          this.setInit(false);
          if (!this.route) {
            this.selectItem(this.route);
          }
          CiRouter.setRouteChangedSelector(this.routeChanged);
        }
        return res;
      }
    },

    methods: {
      loadStores() : void{
        // Load and register events on used server classes
        storeAccidentTravail.registerAccidentTravail();
        storeAdresse.registerAdresse();
        storeApprenti.registerApprenti();
        storeAttestation.registerAttestation();
        storeContact.registerContact();
        storeDocument.registerDocument();
        storeEtablissement.registerEtablissement();
        storeNotif.registerNotif();
        storePaymentMeans.registerPaymentMeans();
        storeQualite.registerQualite();
        storeRib.registerRib();
        storeSalarie.registerSalarie();
        storeTransaction.registerTransaction();
      },
      setInit(value: boolean) : void{
        this.init = value;
      },
      routeChanged (route: Route): void {
        this.selectItem(route);
      },
      selectItem(route: Route | undefined): void {
        const routeElement = ModuleEtablissementOpenFiche(route);
        this.routeElement = routeElement;
        this.selectedFiche = routeElement.fiche;
        this.selectedFiche.ressourceHeader = this.ressource;
        if (route) {
          this.notifRoute = route;
          if (route && route.attribut) {
            this.attribut = route.attribut;
          } else {
            this.attribut = undefined;
          }
          if (route.section && route.section != Sections.NONE) {
            this.lastShowSection = !this.lastShowSection;
            this.selectedFiche.showSection = this.lastShowSection;
          }
        }
        const index = this.lastSelectedFiche.findIndex(item => {
          return item.component === this.selectedFiche.component;
        })
        if (index >= 0) {
          this.lastRoutes.splice(index + 1)
          this.lastSelectedFiche.splice(index + 1)
        } else {
          this.lastRoutes.push(route);
          this.lastSelectedFiche.push(this.selectedFiche);
        }
      }
    }
  })
</script>
